import { registerBlockType } from "@wordpress/blocks";
import {
    BlockControls,
    InnerBlocks,
    useBlockProps,
    InspectorControls,
    PanelColorSettings,
} from "@wordpress/block-editor";

import {
    PanelBody,
    ToolbarButton,
    SelectControl,
    RangeControl,
    ToggleControl,
    TabPanel,
    __experimentalView as View,
} from "@wordpress/components";
import UnitRange from "./components/UnitRange";
import { useState } from "react";
import CustomSelect from "./components/CustomSelect";
import CustomSwitch from "./components/CustomSwitch";

registerBlockType("kamikaze/menu-block", {
    apiVersion: 2,
    title: "Menu",
    icon: "menu",
    category: "design",
    supports: {
        html: false,
        reusable: false,
        align: ["wide", "full"],
    },
    attributes: {
        isSticky: {
            type: "boolean",
            default: false,
        },
        stickyOffset: {
            type: "number",
            default: 500,
        },
        stickyOffsetUnit: {
            type: "string",
            default: "px",
        },
        mobileBreakpoint: {
            type: "number",
            default: 768,
        },
        mobileBehavior: {
            type: "string",
            default: "hamburger",
        },
        hamburgerColor: {
            type: "string",
            default: "#000000",
        },
        savedMenuActionsContent: {
            type: "object",
            default: null,
        },
        showMenuActions: {
            type: "boolean",
            default: true,
        },
        zIndex: {
            type: "number",
            default: 90,
        },
    },

    edit: function MenuEdit({ attributes, setAttributes, clientId }) {
        const { select, dispatch } = wp.data;
        const { createBlock } = wp.blocks;
        const editor = select("core/block-editor");
        const innerBlocks = editor.getBlocks(clientId);

        const handleStickyChange = (value) => {
            setAttributes({ isSticky: value });

            const innerBlocks = select("core/block-editor").getBlocks(clientId);
            const menuActionsBlock = innerBlocks.find(
                (block) => block.name === "kamikaze/menu-actions"
            );

            if (value) {
                if (!menuActionsBlock) {
                    if (attributes.savedMenuActionsContent) {
                        const restoredBlock = createBlock(
                            "kamikaze/menu-actions",
                            attributes.savedMenuActionsContent.attributes,
                            attributes.savedMenuActionsContent.innerBlocks
                        );
                        dispatch("core/block-editor").insertBlock(
                            restoredBlock,
                            innerBlocks.length,
                            clientId
                        );
                    } else {
                        dispatch("core/block-editor").insertBlock(
                            createBlock("kamikaze/menu-actions"),
                            innerBlocks.length,
                            clientId
                        );
                    }
                }
            } else {
                if (menuActionsBlock) {
                    const blockContent = {
                        attributes: menuActionsBlock.attributes,
                        innerBlocks: menuActionsBlock.innerBlocks,
                    };
                    setAttributes({ savedMenuActionsContent: blockContent });

                    dispatch("core/block-editor").removeBlock(
                        menuActionsBlock.clientId
                    );
                }
            }
        };

        const handleMenuActionsVisibility = (value) => {
            setAttributes({ showMenuActions: value });
            const innerBlocks = select("core/block-editor").getBlocks(clientId);
            const menuActionsBlock = innerBlocks.find(
                (block) => block.name === "kamikaze/menu-actions"
            );

            if (menuActionsBlock) {
                dispatch("core/block-editor").updateBlockAttributes(
                    menuActionsBlock.clientId,
                    { isVisible: value }
                );
            }
        };

        const handleAddModalBlock = () => {
            const { createBlock } = wp.blocks;
            const innerBlocks = select("core/block-editor").getBlocks(clientId);

            dispatch("core/block-editor").insertBlock(
                createBlock("kamikaze/modal"),
                innerBlocks.length,
                clientId
            );
        };

        const blockProps = useBlockProps({
            className: `menu-block`,
            style: {
                zIndex: attributes.zIndex,
            },
        });

        return (
            <>
                <InspectorControls>
                    <PanelBody title="Ustawienia menu" initialOpen={true}>
                        <View style={{ padding: "0 0 25px 0", width: "100%" }}>
                            <button
                                type="button"
                                style={{
                                    width: "100%",
                                    borderRadius: "3px",
                                    border: "none",
                                }}
                                className="components-button is-next-40px-default-size is-primary"
                                onClick={handleAddModalBlock}
                            >
                                Dodaj modal
                            </button>
                        </View>
                        <View style={{ padding: "0 0 15px 0" }}>
                            <CustomSwitch
                                label="Sticky menu"
                                checked={attributes.isSticky}
                                onChange={handleStickyChange}
                                size="default"
                            />
                        </View>
                        {attributes.isSticky && (
                            <>
                                <View style={{ padding: "0 0 30px 0" }}>
                                    <CustomSwitch
                                        label="Pokaż sticky menu"
                                        checked={attributes.showMenuActions}
                                        onChange={handleMenuActionsVisibility}
                                        size="default"
                                    />
                                </View>
                                <UnitRange
                                    rangeValue={attributes.stickyOffset}
                                    onRangeChange={(value) =>
                                        setAttributes({
                                            stickyOffset: value,
                                        })
                                    }
                                    unitValue={{
                                        value: attributes.stickyOffset,
                                        unit: attributes.stickyOffsetUnit,
                                    }}
                                    onUnitChange={(newValue) => {
                                        setAttributes({
                                            stickyOffset: newValue.value,
                                            stickyOffsetUnit: newValue.unit,
                                        });
                                    }}
                                    label="Offset przyklejenia"
                                    min={0}
                                    max={1000}
                                    __nextHasNoMarginBottom={true}
                                />
                            </>
                        )}
                        <View style={{ padding: "10px 0" }}>
                            <RangeControl
                                label="Z-Index"
                                value={attributes.zIndex}
                                onChange={(value) =>
                                    setAttributes({ zIndex: value })
                                }
                                min={-1}
                                max={100}
                                __nextHasNoMarginBottom={true}
                            />
                        </View>
                    </PanelBody>
                    <PanelBody title="Ustawienia mobile" initialOpen={false}>
                        <View style={{ padding: "15px 0 0 0" }}>
                            <RangeControl
                                label="Breakpoint mobilny (px)"
                                value={attributes.mobileBreakpoint}
                                onChange={(value) =>
                                    setAttributes({
                                        mobileBreakpoint: value,
                                    })
                                }
                                min={320}
                                max={1200}
                                __nextHasNoMarginBottom={true}
                            />
                            <CustomSelect
                                label="Zachowanie na mobile"
                                value={attributes.mobileBehavior}
                                options={[
                                    {
                                        label: "Hamburger",
                                        value: "hamburger",
                                    },
                                    {
                                        label: "Dropdown",
                                        value: "dropdown",
                                    },
                                    {
                                        label: "Akordeon",
                                        value: "accordion",
                                    },
                                ]}
                                onChange={(event) =>
                                    setAttributes({
                                        mobileBehavior: event.target.value,
                                    })
                                }
                            />
                        </View>
                    </PanelBody>
                </InspectorControls>
                <div {...blockProps}>
                    <InnerBlocks
                        allowedBlocks={[
                            "kamikaze/menu-navigation",
                            "kamikaze/menu-actions",
                            "kamikaze/modal",
                        ]}
                        template={[["kamikaze/menu-navigation"]]}
                        templateLock={false}
                    />
                </div>
            </>
        );
    },

    save: function MenuSave({ attributes }) {
        const blockProps = useBlockProps.save({
            className: `custom-menu-block`,
            style: {
                zIndex: attributes.zIndex,
            },
            "data-is-sticky": attributes.isSticky,
            "data-sticky-offset":
                attributes.stickyOffset + attributes.stickyOffsetUnit,
            "data-mobile-breakpoint": attributes.mobileBreakpoint,
            "data-mobile-behavior": attributes.mobileBehavior,
            "data-menu-width": attributes.menuWidth + attributes.menuWidthUnit,
        });

        return (
            <div {...blockProps}>
                <InnerBlocks.Content />
            </div>
        );
    },
});

registerBlockType("kamikaze/menu-navigation", {
    apiVersion: 2,
    title: "Static Menu",
    icon: "menu",
    category: "design",
    parent: ["kamikaze/menu-block"],
    supports: {
        html: false,
        reusable: false,
    },
    attributes: {
        initialPosition: {
            type: "string",
            default: "relative",
        },
        backgroundColor: {
            type: "string",
            default: "#ffffff",
        },
        textColor: {
            type: "string",
            default: "#000000",
        },
        hoverColor: {
            type: "string",
            default: "#f0f0f0",
        },
        fontSize: {
            type: "number",
            default: 16,
        },
        fontSizeUnit: {
            type: "string",
            default: "px",
        },
        itemSpacing: {
            type: "number",
            default: 20,
        },
        itemSpacingUnit: {
            type: "string",
            default: "px",
        },
        menuPadding: {
            type: "object",
            default: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 20,
            },
        },
        menuBorderRadius: {
            type: "number",
            default: 0,
        },
        showDropdownIcon: {
            type: "boolean",
            default: true,
        },
        dropdownIconColor: {
            type: "string",
            default: "#000000",
        },
        animation: {
            type: "string",
            default: "none",
        },
        animationDuration: {
            type: "number",
            default: 0.3,
        },
        boxShadow: {
            type: "boolean",
            default: false,
        },
        boxShadowColor: {
            type: "string",
            default: "rgba(0,0,0,0.1)",
        },
        menuHeight: {
            type: "number",
            default: 4,
        },
        menuWidth: {
            type: "number",
            default: 100,
        },
        menuHeightUnit: {
            type: "string",
            default: "vw",
        },
        menuWidthUnit: {
            type: "string",
            default: "%",
        },
        contentPosition: {
            type: "string",
            default: "top",
        },
        boxShadowOffsetX: {
            type: "number",
            default: 0,
        },
        boxShadowOffsetY: {
            type: "number",
            default: 0,
        },
        boxShadowBlur: {
            type: "number",
            default: 0,
        },
        boxShadowSpread: {
            type: "number",
            default: 0,
        },
        zIndex: {
            type: "number",
            default: 90,
        },
    },
    edit: function MenuNavigationEdit({ attributes, setAttributes }) {
        return (
            <>
                <InspectorControls>
                    <TabPanel
                        className="custom-tab-panel"
                        tabs={[
                            { name: "general", title: "General" },
                            { name: "style", title: "Styles" },
                        ]}
                    >
                        {(tab) => {
                            if (tab.name === "general") {
                                return (
                                    <View
                                        style={{
                                            padding: "5px 15px 15px 15px",
                                        }}
                                    >
                                        <View
                                            style={{
                                                padding: "0 0 10px 0",
                                            }}
                                        >
                                            <CustomSelect
                                                label="Pozycja początkowa"
                                                value={
                                                    attributes.initialPosition
                                                }
                                                onChange={(event) =>
                                                    setAttributes({
                                                        initialPosition:
                                                            event.target.value,
                                                    })
                                                }
                                                options={[
                                                    {
                                                        value: "relative",
                                                        label: "Relative",
                                                    },
                                                    {
                                                        value: "absolute",
                                                        label: "Absolute",
                                                    },
                                                    {
                                                        value: "fixed",
                                                        label: "Fixed",
                                                    },
                                                ]}
                                            />
                                        </View>
                                        <UnitRange
                                            rangeValue={attributes.menuHeight}
                                            onRangeChange={(value) =>
                                                setAttributes({
                                                    menuHeight: value,
                                                })
                                            }
                                            unitValue={{
                                                value: attributes.menuHeight,
                                                unit: attributes.menuHeightUnit,
                                            }}
                                            onUnitChange={(newValue) => {
                                                setAttributes({
                                                    menuHeight: newValue.value,
                                                    menuHeightUnit:
                                                        newValue.unit,
                                                });
                                            }}
                                            label="Wysokość menu"
                                            min={0}
                                            max={100}
                                            __nextHasNoMarginBottom={true}
                                            style={{
                                                marginBottom: "15px",
                                            }}
                                        />
                                        <UnitRange
                                            rangeValue={attributes.menuWidth}
                                            onRangeChange={(value) =>
                                                setAttributes({
                                                    menuWidth: value,
                                                })
                                            }
                                            unitValue={{
                                                value: attributes.menuWidth,
                                                unit: attributes.menuWidthUnit,
                                            }}
                                            onUnitChange={(newValue) => {
                                                setAttributes({
                                                    menuWidth: newValue.value,
                                                    menuWidthUnit:
                                                        newValue.unit,
                                                });
                                            }}
                                            label="Szerokość menu"
                                            min={0}
                                            max={100}
                                            __nextHasNoMarginBottom={true}
                                            style={{
                                                marginBottom: "15px",
                                            }}
                                        />
                                        <UnitRange
                                            rangeValue={attributes.itemSpacing}
                                            onRangeChange={(value) =>
                                                setAttributes({
                                                    itemSpacing: value,
                                                })
                                            }
                                            unitValue={{
                                                value: attributes.itemSpacing,
                                                unit: attributes.itemSpacingUnit,
                                            }}
                                            onUnitChange={(newValue) => {
                                                setAttributes({
                                                    itemSpacing: newValue.value,
                                                    itemSpacingUnit:
                                                        newValue.unit,
                                                });
                                            }}
                                            label="Odstęp między elementami"
                                            min={0}
                                            max={100}
                                            __nextHasNoMarginBottom={true}
                                            style={{
                                                marginBottom: "15px",
                                            }}
                                        />
                                        <View
                                            style={{
                                                padding: "15px 0px 10px 0",
                                                marginBottom: "15px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "space-between",
                                                }}
                                            >
                                                <CustomSwitch
                                                    checked={
                                                        attributes.showDropdownIcon
                                                    }
                                                    onChange={(e) =>
                                                        setAttributes({
                                                            showDropdownIcon:
                                                                e.target
                                                                    .checked,
                                                        })
                                                    }
                                                    label="Pokaż ikonę dropdown"
                                                    size="default"
                                                />
                                            </div>
                                        </View>
                                        {attributes.showDropdownIcon && (
                                            <PanelColorSettings
                                                title="Kolor ikony dropdown"
                                                initialOpen={false}
                                                enableAlpha={true}
                                                colorSettings={[
                                                    {
                                                        value: attributes.dropdownIconColor,
                                                        onChange: (color) =>
                                                            setAttributes({
                                                                dropdownIconColor:
                                                                    color,
                                                            }),
                                                        label: "Kolor",
                                                    },
                                                ]}
                                            />
                                        )}
                                        <View
                                            style={{
                                                padding: "10px 0",
                                            }}
                                        >
                                            <CustomSelect
                                                label="Animacja"
                                                value={attributes.animation}
                                                options={[
                                                    {
                                                        label: "Brak",
                                                        value: "none",
                                                    },
                                                    {
                                                        label: "Fade",
                                                        value: "fade",
                                                    },
                                                    {
                                                        label: "Slide",
                                                        value: "slide",
                                                    },
                                                ]}
                                                onChange={(event) =>
                                                    setAttributes({
                                                        animation:
                                                            event.target.value,
                                                    })
                                                }
                                            />
                                        </View>
                                        {attributes.animation !== "none" && (
                                            <RangeControl
                                                label="Czas trwania animacji (s)"
                                                value={
                                                    attributes.animationDuration
                                                }
                                                onChange={(value) =>
                                                    setAttributes({
                                                        animationDuration:
                                                            value,
                                                    })
                                                }
                                                min={0.1}
                                                max={2}
                                                step={0.1}
                                                __nextHasNoMarginBottom={true}
                                            />
                                        )}
                                        <View
                                            style={{
                                                padding: "10px 0 15px 0",
                                            }}
                                        >
                                            <CustomSwitch
                                                label="Cień"
                                                checked={attributes.boxShadow}
                                                onChange={(e) =>
                                                    setAttributes({
                                                        boxShadow:
                                                            e.target.checked,
                                                    })
                                                }
                                                size="default"
                                            />
                                        </View>
                                        {attributes.boxShadow && (
                                            <View
                                                style={{
                                                    marginBottom: "15px",
                                                }}
                                            >
                                                <PanelColorSettings
                                                    title="Kolor cienia"
                                                    initialOpen={false}
                                                    enableAlpha={true}
                                                    colorSettings={[
                                                        {
                                                            value: attributes.boxShadowColor,
                                                            onChange: (color) =>
                                                                setAttributes({
                                                                    boxShadowColor:
                                                                        color,
                                                                }),
                                                            label: "Kolor",
                                                        },
                                                    ]}
                                                />
                                                <RangeControl
                                                    label="Przesunięcie X"
                                                    value={
                                                        attributes.boxShadowOffsetX
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            boxShadowOffsetX:
                                                                value,
                                                        })
                                                    }
                                                    min={-50}
                                                    max={50}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                    style={{
                                                        marginBottom: "15px",
                                                    }}
                                                />
                                                <RangeControl
                                                    label="Przesunięcie Y"
                                                    value={
                                                        attributes.boxShadowOffsetY
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            boxShadowOffsetY:
                                                                value,
                                                        })
                                                    }
                                                    min={-50}
                                                    max={50}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                    style={{
                                                        marginBottom: "15px",
                                                    }}
                                                />
                                                <RangeControl
                                                    label="Rozmycie"
                                                    value={
                                                        attributes.boxShadowBlur
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            boxShadowBlur:
                                                                value,
                                                        })
                                                    }
                                                    min={0}
                                                    max={100}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                    style={{
                                                        marginBottom: "15px",
                                                    }}
                                                />
                                                <RangeControl
                                                    label="Rozszerzenie"
                                                    value={
                                                        attributes.boxShadowSpread
                                                    }
                                                    onChange={(value) =>
                                                        setAttributes({
                                                            boxShadowSpread:
                                                                value,
                                                        })
                                                    }
                                                    min={-50}
                                                    max={50}
                                                    __nextHasNoMarginBottom={
                                                        true
                                                    }
                                                    style={{
                                                        marginBottom: "15px",
                                                    }}
                                                />
                                            </View>
                                        )}
                                        <View
                                            style={{
                                                padding: "10px 0",
                                            }}
                                        >
                                            <RangeControl
                                                label="Zaokrąglenie rogów"
                                                value={
                                                    attributes.menuBorderRadius
                                                }
                                                onChange={(value) =>
                                                    setAttributes({
                                                        menuBorderRadius: value,
                                                    })
                                                }
                                                min={0}
                                                max={50}
                                                __nextHasNoMarginBottom={true}
                                                style={{
                                                    marginBottom: "15px",
                                                }}
                                            />
                                        </View>
                                        <View style={{ padding: "10px 0" }}>
                                            <RangeControl
                                                label="Z-Index"
                                                value={attributes.zIndex}
                                                onChange={(value) =>
                                                    setAttributes({
                                                        zIndex: value,
                                                    })
                                                }
                                                min={-1}
                                                max={999}
                                                __nextHasNoMarginBottom={true}
                                                style={{
                                                    marginBottom: "15px",
                                                }}
                                            />
                                        </View>
                                    </View>
                                );
                            } else if (tab.name === "style") {
                                return (
                                    <View
                                        style={{
                                            padding: "5px 15px 15px 15px",
                                        }}
                                    >
                                        <PanelColorSettings
                                            title="Kolory"
                                            initialOpen={true}
                                            enableAlpha={true}
                                            colorSettings={[
                                                {
                                                    value: attributes.backgroundColor,
                                                    onChange: (color) =>
                                                        setAttributes({
                                                            backgroundColor:
                                                                color,
                                                        }),
                                                    label: "Tło",
                                                },
                                                {
                                                    value: attributes.textColor,
                                                    onChange: (color) =>
                                                        setAttributes({
                                                            textColor: color,
                                                        }),
                                                    label: "Tekst",
                                                },
                                                {
                                                    value: attributes.hoverColor,
                                                    onChange: (color) =>
                                                        setAttributes({
                                                            hoverColor: color,
                                                        }),
                                                    label: "Hover",
                                                },
                                            ]}
                                        />
                                        <UnitRange
                                            rangeValue={attributes.fontSize}
                                            onRangeChange={(value) =>
                                                setAttributes({
                                                    fontSize: value,
                                                })
                                            }
                                            unitValue={{
                                                value: attributes.fontSize,
                                                unit: attributes.fontSizeUnit,
                                            }}
                                            onUnitChange={(newValue) => {
                                                setAttributes({
                                                    fontSize: newValue.value,
                                                    fontSizeUnit: newValue.unit,
                                                });
                                            }}
                                            label="Rozmiar czcionki"
                                            min={8}
                                            max={72}
                                            __nextHasNoMarginBottom={true}
                                        />
                                    </View>
                                );
                            }
                        }}
                    </TabPanel>
                </InspectorControls>
                <div className="menu-navigation">
                    <InnerBlocks
                        allowedBlocks={["core/group"]}
                        template={[["core/group"]]}
                        templateLock={false}
                    />
                </div>
            </>
        );
    },
    save: function MenuNavigationSave({ attributes }) {
        const navigationStyle = {
            position: attributes.initialPosition,
            backgroundColor: attributes.backgroundColor,
            "--menu-color": attributes.textColor,
            fontSize: attributes.fontSize + attributes.fontSizeUnit,
            gap: attributes.itemSpacing + attributes.itemSpacingUnit,
            height: attributes.menuHeight + attributes.menuHeightUnit,
            width: attributes.menuWidth + attributes.menuWidthUnit,
            borderRadius: attributes.menuBorderRadius + "px",
            zIndex: attributes.zIndex,
            ...(attributes.boxShadow && {
                boxShadow: `${attributes.boxShadowOffsetX}px ${attributes.boxShadowOffsetY}px ${attributes.boxShadowBlur}px ${attributes.boxShadowSpread}px ${attributes.boxShadowColor}`,
            }),
        };

        const blockProps = useBlockProps.save({
            className: "menu-navigation",
            style: navigationStyle,
            "data-initial-position": attributes.initialPosition,
            "data-hover-color": attributes.hoverColor,
            "data-show-dropdown-icon": attributes.showDropdownIcon,
            "data-dropdown-icon-color": attributes.dropdownIconColor,
            "data-animation": attributes.animation,
            "data-animation-duration": attributes.animationDuration,
            "data-box-shadow": attributes.boxShadow,
            "data-box-shadow-color": attributes.boxShadowColor,
            "data-menu-width": attributes.menuWidth + attributes.menuWidthUnit,
        });

        return (
            <div {...blockProps}>
                <InnerBlocks.Content />
            </div>
        );
    },
});

registerBlockType("kamikaze/menu-actions", {
    apiVersion: 2,
    title: "Menu Sticky",
    description: "Blok menu dla trybu sticky",
    category: "design",
    icon: "menu",
    supports: {
        html: false,
        anchor: true,
    },
    parent: ["kamikaze/menu-block"],
    attributes: {
        stickyBackgroundColor: {
            type: "string",
            default: "#ffffff",
        },
        stickyHeight: {
            type: "number",
            default: 4,
        },
        stickyHeightUnit: {
            type: "string",
            default: "vw",
        },
        stickyWidth: {
            type: "number",
            default: 100,
        },
        stickyWidthUnit: {
            type: "string",
            default: "%",
        },
        boxShadow: {
            type: "boolean",
            default: false,
        },
        boxShadowColor: {
            type: "string",
            default: "rgba(0,0,0,0.1)",
        },
        boxShadowOffsetX: {
            type: "number",
            default: 0,
        },
        boxShadowOffsetY: {
            type: "number",
            default: 0,
        },
        boxShadowBlur: {
            type: "number",
            default: 0,
        },
        boxShadowSpread: {
            type: "number",
            default: 0,
        },
        showProgressBar: {
            type: "boolean",
            default: false,
        },
        progressBarColor: {
            type: "string",
            default: "#1976d2",
        },
        progressBarHeight: {
            type: "number",
            default: 4,
        },
        progressBarHeightUnit: {
            type: "string",
            default: "px",
        },
        isVisible: {
            type: "boolean",
            default: true,
        },
    },

    edit: function MenuActionsEdit({ attributes, setAttributes }) {
        const blockProps = useBlockProps({
            className: "menu-actions",
            style: {
                backgroundColor: attributes.stickyBackgroundColor,
                height: attributes.stickyHeight + attributes.stickyHeightUnit,
                width: attributes.stickyWidth + attributes.stickyWidthUnit,
                display: attributes.isVisible ? "block" : "none",
                "--height":
                    attributes.stickyHeight + attributes.stickyHeightUnit,
                ...(attributes.boxShadow && {
                    boxShadow: `${attributes.boxShadowOffsetX}px ${attributes.boxShadowOffsetY}px ${attributes.boxShadowBlur}px ${attributes.boxShadowSpread}px ${attributes.boxShadowColor}`,
                }),
            },
        });

        return (
            <>
                <InspectorControls>
                    <PanelBody
                        title="Ustawienia sticky menu"
                        initialOpen={true}
                    >
                        <View style={{ padding: "5px 0 15px 0" }}>
                            <UnitRange
                                rangeValue={attributes.stickyHeight}
                                onRangeChange={(value) =>
                                    setAttributes({
                                        stickyHeight: value,
                                    })
                                }
                                unitValue={{
                                    value: attributes.stickyHeight,
                                    unit: attributes.stickyHeightUnit,
                                }}
                                onUnitChange={(newValue) => {
                                    setAttributes({
                                        stickyHeight: newValue.value,
                                        stickyHeightUnit: newValue.unit,
                                    });
                                }}
                                label="Wysokość menu"
                                min={0}
                                max={1000}
                                __nextHasNoMarginBottom={true}
                            />
                            <UnitRange
                                rangeValue={attributes.stickyWidth}
                                onRangeChange={(value) =>
                                    setAttributes({
                                        stickyWidth: value,
                                    })
                                }
                                unitValue={{
                                    value: attributes.stickyWidth,
                                    unit: attributes.stickyWidthUnit,
                                }}
                                onUnitChange={(newValue) => {
                                    setAttributes({
                                        stickyWidth: newValue.value,
                                        stickyWidthUnit: newValue.unit,
                                    });
                                }}
                                label="Szerokość menu"
                                min={0}
                                max={100}
                                __nextHasNoMarginBottom={true}
                            />
                        </View>
                        <PanelColorSettings
                            title="Kolor tła"
                            initialOpen={false}
                            colorSettings={[
                                {
                                    value: attributes.stickyBackgroundColor,
                                    onChange: (color) =>
                                        setAttributes({
                                            stickyBackgroundColor: color,
                                        }),
                                    label: "Tło",
                                },
                            ]}
                        />
                    </PanelBody>
                    <PanelBody title="Ustawienia cienia" initialOpen={false}>
                        <View style={{ padding: "10px 15px" }}>
                            <CustomSwitch
                                label="Dodaj cień"
                                checked={attributes.boxShadow}
                                onChange={(e) =>
                                    setAttributes({
                                        boxShadow: e.target.checked,
                                    })
                                }
                                size="default"
                            />
                            {attributes.boxShadow && (
                                <>
                                    <RangeControl
                                        label="Przesunięcie X"
                                        value={attributes.boxShadowOffsetX}
                                        onChange={(value) =>
                                            setAttributes({
                                                boxShadowOffsetX: value,
                                            })
                                        }
                                        min={-50}
                                        max={50}
                                        __nextHasNoMarginBottom={true}
                                    />
                                    <RangeControl
                                        label="Przesunięcie Y"
                                        value={attributes.boxShadowOffsetY}
                                        onChange={(value) =>
                                            setAttributes({
                                                boxShadowOffsetY: value,
                                            })
                                        }
                                        min={-50}
                                        max={50}
                                        __nextHasNoMarginBottom={true}
                                    />
                                    <RangeControl
                                        label="Rozmycie"
                                        value={attributes.boxShadowBlur}
                                        onChange={(value) =>
                                            setAttributes({
                                                boxShadowBlur: value,
                                            })
                                        }
                                        min={0}
                                        max={100}
                                        __nextHasNoMarginBottom={true}
                                    />
                                    <RangeControl
                                        label="Rozszerzenie"
                                        value={attributes.boxShadowSpread}
                                        onChange={(value) =>
                                            setAttributes({
                                                boxShadowSpread: value,
                                            })
                                        }
                                        min={-50}
                                        max={50}
                                        __nextHasNoMarginBottom={true}
                                    />
                                </>
                            )}
                        </View>
                        {attributes.boxShadow && (
                            <View
                                style={{
                                    marginBottom: "15px",
                                }}
                            >
                                <PanelColorSettings
                                    title="Kolor cienia"
                                    initialOpen={false}
                                    enableAlpha={true}
                                    colorSettings={[
                                        {
                                            value: attributes.boxShadowColor,
                                            onChange: (color) =>
                                                setAttributes({
                                                    boxShadowColor: color,
                                                }),
                                            label: "Kolor",
                                        },
                                    ]}
                                />
                            </View>
                        )}
                    </PanelBody>
                    <PanelBody title="Progress Bar" initialOpen={false}>
                        <View style={{ padding: "10px 0px" }}>
                            <View style={{ padding: "10px 0px" }}>
                                <CustomSwitch
                                    label="Pokaż progress bar"
                                    checked={attributes.showProgressBar}
                                    onChange={(e) =>
                                        setAttributes({
                                            showProgressBar: e.target.checked,
                                        })
                                    }
                                    size="default"
                                />
                            </View>

                            {attributes.showProgressBar && (
                                <>
                                    <UnitRange
                                        rangeValue={
                                            attributes.progressBarHeight
                                        }
                                        onRangeChange={(value) =>
                                            setAttributes({
                                                progressBarHeight: value,
                                            })
                                        }
                                        unitValue={{
                                            value: attributes.progressBarHeight,
                                            unit: attributes.progressBarHeightUnit,
                                        }}
                                        onUnitChange={(newValue) => {
                                            setAttributes({
                                                progressBarHeight:
                                                    newValue.value,
                                                progressBarHeightUnit:
                                                    newValue.unit,
                                            });
                                        }}
                                        label="Wysokość"
                                        min={1}
                                        max={50}
                                        __nextHasNoMarginBottom={true}
                                    />
                                    <PanelColorSettings
                                        title="Kolor progress bar"
                                        initialOpen={false}
                                        enableAlpha={true}
                                        colorSettings={[
                                            {
                                                value: attributes.progressBarColor,
                                                onChange: (color) =>
                                                    setAttributes({
                                                        progressBarColor: color,
                                                    }),
                                                label: "Kolor",
                                            },
                                        ]}
                                    />
                                </>
                            )}
                        </View>
                    </PanelBody>
                </InspectorControls>
                <div {...blockProps}>
                    <div
                        style={{
                            display: "block",
                        }}
                    >
                        <InnerBlocks
                            allowedBlocks={["core/group"]}
                            template={[["core/group"]]}
                            templateLock={false}
                        />
                    </div>
                </div>
            </>
        );
    },

    save: function MenuActionsSave({ attributes }) {
        const actionStyle = {
            backgroundColor: attributes.stickyBackgroundColor,
            height: attributes.stickyHeight + attributes.stickyHeightUnit,
            width: attributes.stickyWidth + attributes.stickyWidthUnit,
            display: attributes.isVisible ? "flex" : "none",
            "--height": attributes.stickyHeight + attributes.stickyHeightUnit,
            ...(attributes.boxShadow && {
                boxShadow: `${attributes.boxShadowOffsetX}px ${attributes.boxShadowOffsetY}px ${attributes.boxShadowBlur}px ${attributes.boxShadowSpread}px ${attributes.boxShadowColor}`,
            }),
        };

        const blockProps = useBlockProps.save({
            className: "menu-actions",
            style: actionStyle,
            "data-sticky-background-color": attributes.stickyBackgroundColor,
            "data-sticky-height":
                attributes.stickyHeight + attributes.stickyHeightUnit,
            "data-sticky-width":
                attributes.stickyWidth + attributes.stickyWidthUnit,
            "data-box-shadow": attributes.boxShadow,
            "data-box-shadow-color": attributes.boxShadowColor,
            "data-show-progress-bar": attributes.showProgressBar,
            "data-progress-bar-color": attributes.progressBarColor,
            "data-progress-bar-height": attributes.progressBarHeight,
        });

        return (
            <div {...blockProps}>
                <div className="menu-actions-wrapper">
                    <InnerBlocks.Content />
                </div>
            </div>
        );
    },
});
